/**
 * shared/pmove/ladder.ts — PM_CheckLadderMove / PM_LadderMove.
 *
 * OWNER: PMOVE agent.
 * T6 PM_CheckLadderMove 0x946eb0 / PM_LadderMove 0x947930 [PM §2, §3 "Ladder"]: probe 30 u ahead
 * (8 u ...); ladder surfaces carry surface flag 0x8 (SURF.LADDER, ESTIMATE: 8 thin playerclip
 * brushes on Hijacked have it). Body is [T5 bg_pmove.cpp:PM_CheckLadderMove, PM_LadderMove].
 */
import { createTraceResult, SURF } from '../collision/types';
import { ENTITYNUM_NONE } from '../constants';
import { type Vec3, vnormalize } from '../math';
import { PMF, PmType, type PlayerState } from '../types';
import { checkJump } from './jump';
import { accelerate, airMove, cmdScaleAir } from './pmove';
import { stepSlideMove } from './slide';
import { angleDelta, effectiveStance, pmTrace, PM_LADDER_SIDE_FRICTION, PM_STANCE_PRONE, vec2Normalize, vecToYaw, type Pm } from './pm';

/** PM_ClearLadderFlag: leaving a ladder sets LADDER_FALL until you walk again. */
export function clearLadderFlag(ps: PlayerState): void {
  if (ps.pm_flags & PMF.LADDER) {
    ps.pm_flags |= PMF.LADDER_FALL;
    ps.pm_flags &= ~PMF.LADDER;
  }
}

const tr = createTraceResult();
const checkDir: Vec3 = [0, 0, 0];
const mins: Vec3 = [0, 0, 0];
const maxs: Vec3 = [0, 0, 0];
const spot: Vec3 = [0, 0, 0];

/** PM_CheckLadderMove [T5 bg_pmove.cpp:5213]. */
export function checkLadderMove(pm: Pm): void {
  const ps = pm.ps;
  if (pm.walking || ps.waterlevel >= 3) ps.pm_flags &= ~PMF.LADDER_FALL;
  if (ps.pm_time && !(ps.pm_flags & PMF.LADDER) && (ps.pm_flags & (PMF.TIME_SLOWDOWN | PMF.TIME_KNOCKBACK))) return;
  const tracedist = pm.walking ? 8 : 30;
  const fellOffLadderInAir = (ps.pm_flags & PMF.LADDER) !== 0 && ps.groundEntityNum === ENTITYNUM_NONE;
  if (fellOffLadderInAir) {
    checkDir[0] = -ps.vLadderVec[0]; checkDir[1] = -ps.vLadderVec[1]; checkDir[2] = -ps.vLadderVec[2];
  } else {
    checkDir[0] = pm.forward[0]; checkDir[1] = pm.forward[1]; checkDir[2] = 0;
    vnormalize(checkDir);
  }
  if (ps.pm_type >= PmType.FREEZE) {
    ps.groundEntityNum = ENTITYNUM_NONE;
    pm.groundPlane = false;
    pm.almostGroundPlane = false;
    pm.walking = false;
    clearLadderFlag(ps);
    return;
  }
  if ((ps.pm_flags & PMF.LADDER_FALL) || effectiveStance(ps) === PM_STANCE_PRONE || pm.cmd.serverTime - ps.jumpTime < 300) {
    clearLadderFlag(ps);
    return;
  }
  mins[0] = pm.mins[0] + 6; mins[1] = pm.mins[1] + 6; mins[2] = 8;
  maxs[0] = pm.maxs[0] - 6; maxs[1] = pm.maxs[1] - 6; maxs[2] = Math.max(pm.maxs[2], 8);
  spot[0] = ps.origin[0] + tracedist * checkDir[0];
  spot[1] = ps.origin[1] + tracedist * checkDir[1];
  spot[2] = ps.origin[2] + tracedist * checkDir[2];
  pmTrace(pm, tr, ps.origin, mins, maxs, spot);
  if (tr.fraction >= 1 || !(tr.surfaceFlags & SURF.LADDER) || (pm.walking && pm.cmd.forwardmove <= 0)) {
    clearLadderFlag(ps);
    return;
  }
  if (ps.pm_flags & PMF.LADDER) {
    ps.pm_flags |= PMF.LADDER;
    return;
  }
  ps.vLadderVec[0] = tr.normal[0]; ps.vLadderVec[1] = tr.normal[1]; ps.vLadderVec[2] = tr.normal[2];
  spot[0] = ps.origin[0] - tracedist * ps.vLadderVec[0];
  spot[1] = ps.origin[1] - tracedist * ps.vLadderVec[1];
  spot[2] = ps.origin[2] - tracedist * ps.vLadderVec[2];
  pmTrace(pm, tr, ps.origin, mins, maxs, spot);
  if (tr.fraction < 1 && (tr.surfaceFlags & SURF.LADDER)) ps.pm_flags |= PMF.LADDER;
  else clearLadderFlag(ps);
}

const wishvel: Vec3 = [0, 0, 0];
const wishdir: Vec3 = [0, 0, 0];
const tempRight: Vec3 = [0, 0, 0];
const sideDir: Vec3 = [0, 0, 0];

/** PM_LadderMove [T5 bg_pmove.cpp:5358]: climb at half speed, look up/down to climb up/down. */
export function ladderMove(pm: Pm): void {
  const ps = pm.ps;
  if (checkJump(pm)) {
    airMove(pm);
    return;
  }
  let upscale = (pm.forward[2] + 0.25) * 2.5;
  if (upscale > 1) upscale = 1;
  else if (upscale < -1) upscale = -1;
  pm.forward[2] = 0;
  vnormalize(pm.forward);
  tempRight[0] = pm.right[0]; tempRight[1] = pm.right[1]; tempRight[2] = 0;
  vnormalize(tempRight);
  // ProjectPointOnPlane(right, vLadderVec)
  const lv = ps.vLadderVec;
  const d = tempRight[0] * lv[0] + tempRight[1] * lv[1] + tempRight[2] * lv[2];
  pm.right[0] = tempRight[0] - d * lv[0]; pm.right[1] = tempRight[1] - d * lv[1]; pm.right[2] = tempRight[2] - d * lv[2];
  const scale = cmdScaleAir(pm);
  wishvel[0] = wishvel[1] = wishvel[2] = 0;
  if (pm.cmd.forwardmove) wishvel[2] = 0.5 * upscale * scale * pm.cmd.forwardmove; // pm_ladderScale 0.5
  if (pm.cmd.rightmove) {
    const side = 0.2 * scale * pm.cmd.rightmove;
    wishvel[0] += side * pm.right[0]; wishvel[1] += side * pm.right[1]; wishvel[2] += side * pm.right[2];
  }
  wishdir[0] = wishvel[0]; wishdir[1] = wishvel[1]; wishdir[2] = wishvel[2];
  const wishspeed = vnormalize(wishdir);
  accelerate(pm, wishdir, wishspeed, 9);
  if (!pm.cmd.forwardmove) {
    // gravity / stop on the ladder
    if (ps.velocity[2] <= 0) {
      ps.velocity[2] += ps.gravity * pm.frametime;
      if (ps.velocity[2] > 0) ps.velocity[2] = 0;
    } else {
      ps.velocity[2] -= ps.gravity * pm.frametime;
      if (ps.velocity[2] < 0) ps.velocity[2] = 0;
    }
  }
  if (!pm.cmd.rightmove) {
    sideDir[0] = pm.right[0]; sideDir[1] = pm.right[1]; sideDir[2] = 0;
    vec2Normalize(sideDir);
    let fSideSpeed = sideDir[0] * ps.velocity[0] + sideDir[1] * ps.velocity[1];
    if (fSideSpeed !== 0) {
      ps.velocity[0] -= fSideSpeed * sideDir[0];
      ps.velocity[1] -= fSideSpeed * sideDir[1];
      let fSpeedDrop = fSideSpeed * pm.frametime * PM_LADDER_SIDE_FRICTION;
      if (Math.abs(fSideSpeed) > Math.abs(fSpeedDrop)) {
        if (Math.abs(fSpeedDrop) < 1) fSpeedDrop = fSpeedDrop < 0 ? -1 : 1;
        fSideSpeed -= fSpeedDrop;
        ps.velocity[0] += fSideSpeed * sideDir[0];
        ps.velocity[1] += fSideSpeed * sideDir[1];
      }
    }
  }
  if (!pm.walking) {
    // stick to the ladder
    const fSideSpeed = lv[0] * ps.velocity[0] + lv[1] * ps.velocity[1];
    ps.velocity[0] -= fSideSpeed * lv[0];
    ps.velocity[1] -= fSideSpeed * lv[1];
    const v = ps.velocity;
    if ((v[0] !== 0 || v[1] !== 0 || v[2] !== 0) && v[2] * v[2] >= v[0] * v[0] + v[1] * v[1]) {
      v[0] += -50 * lv[0];
      v[1] += -50 * lv[1];
    }
  }
  stepSlideMove(pm, false);
  let moveyaw = Math.trunc(angleDelta(vecToYaw(lv) + 180, ps.viewangles[1]));
  if (Math.abs(moveyaw) > 75) moveyaw = moveyaw > 0 ? 75 : -75;
  ps.movementDir = moveyaw & 0xff;
}
